import React from "react";
import { SVFaksimile, CVFaksimile, KVFaksimile } from "./faksimile";

/**
 * Iter 20.10 — Intro-Slides und Story-Panel-Inhalte pro Case.
 *
 * Jeder Case hat:
 *   • introSlides[] — 5 Slides für die Vollbild-Slideshow VOR dem Tool
 *   • storyPanels[] — 4 Panel-Inhalte für den Split-Screen-Modus IM Tool
 *
 * Slides sind React-Nodes (Content wird direkt gerendert). Panels sind
 * Objects mit title/summary/bullets/hint.
 */

/* ═══════════════════════════════════════════════════════════════════════
 * SARAH — Standard-Fall
 * ═══════════════════════════════════════════════════════════════════════ */
export const SARAH_STORY = {
  introSlides: [
    {
      title: "Sarah startet ihren neuen Job",
      subtitle: "Standard-Einstufung nach KV Handel — Angestellte",
      body: (
        <div className="space-y-4 max-w-2xl">
          <p className="text-base text-ink-800 leading-relaxed">
            <strong className="text-ink-900">Sarah</strong> beginnt am{" "}
            <strong>22. Juli 2026</strong> als <strong>Verkäuferin</strong> bei
            Fashion Store GmbH. Die HR-Abteilung muss sie korrekt einstufen —
            nach dem geltenden Kollektivvertrag und mit ihrer bisherigen
            Berufserfahrung.
          </p>
          <p className="text-sm text-warm-700 leading-relaxed">
            Sie sehen jetzt, wie ET diesen Standard-Fall in unter 2 Minuten löst
            — von der KV-Auswahl bis zur fertigen Empfehlung inkl. PDF-Bericht.
          </p>
          <div className="pt-2 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold">
              KV Handel — Angestellte
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold">
              2 Vordienstzeiten
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold">
              ≈ 90 Sekunden
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Ihre bisherige Berufserfahrung",
      subtitle: "Zwei einschlägige Vordienstzeiten als Verkäuferin",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Sarah hat vor ihrem Wechsel schon in zwei Einzelhandels-Unternehmen
              gearbeitet — beide Male als Verkäuferin. Diese Positionen sind
              einschlägig zum neuen Job und werden im KV Handel voll angerechnet.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              Die HR erhält typischerweise einen Lebenslauf und einen SV-Auszug
              als Grundlage. Der SV-Auszug ist dabei die verbindliche
              Datenquelle.
            </p>
          </div>
          <CVFaksimile
            personName="Sarah Musterhuber"
            subtitle="Verkäuferin · Einzelhandel"
            positions={[
              {
                zeitraum: "03/2022 – 08/2024",
                arbeitgeber: "Fashion Store GmbH",
                taetigkeit: "Verkäuferin Einzelhandel",
              },
              {
                zeitraum: "09/2020 – 02/2022",
                arbeitgeber: "Mode Bertl KG",
                taetigkeit: "Verkäuferin Einzelhandel",
              },
            ]}
            education={[
              {
                zeitraum: "2016 – 2020",
                einrichtung: "HAK Wien",
                abschluss: "Matura",
              },
            ]}
          />
        </div>
      ),
    },
    {
      title: "Der SV-Auszug — Sarahs Datenquelle",
      subtitle: "Sozialversicherungsdaten sind rechtlich verbindlich",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Ein <strong>SV-Auszug</strong> listet alle
              sozialversicherungspflichtigen Beschäftigungen einer Person
              — mit exaktem Zeitraum, Dienstgeber und Beschäftigungsart.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              ET liest den SV-Auszug automatisch, erkennt jede Zeile als
              Vordienstzeit und schlägt den Anrechnungsfaktor gemäß KV-Regel
              vor. Sie können jede Zeile manuell prüfen.
            </p>
            <div className="text-[11px] text-warm-500 pt-1">
              → Auf der rechten Seite sehen Sie ein anonymisiertes Beispiel.
            </div>
          </div>
          <SVFaksimile
            personName="Musterhuber, Sarah"
            highlightIndex={0}
            rows={[
              {
                von: "01.03.2022",
                bis: "31.08.2024",
                arbeitgeber: "Fashion Store GmbH",
                art: "Angestellt · Vollzeit",
              },
              {
                von: "01.09.2020",
                bis: "28.02.2022",
                arbeitgeber: "Mode Bertl KG",
                art: "Angestellt · Vollzeit",
              },
              {
                von: "01.07.2020",
                bis: "31.08.2020",
                arbeitgeber: "Sommerjob GmbH",
                art: "Geringfügig · befristet",
              },
            ]}
          />
        </div>
      ),
    },
    {
      title: "Der Kollektivvertrag regelt die Anrechnung",
      subtitle: "KV Handel/Angestellte — Gehaltsstufen & Anrechnungsregeln",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Jeder Kollektivvertrag hat eigene Regeln für{" "}
              <strong>Beschäftigungsgruppen</strong>, <strong>Gehaltsstufen</strong>{" "}
              und <strong>Anrechnungsfaktoren</strong>, meist mit einer{" "}
              <strong>Höchstanrechnungsdauer</strong>. ET kennt die aktuellen
              KV-Regeln und wendet sie automatisch an.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              Im Handel/Angestellte werden einschlägige Angestelltenzeiten
              zu 100 % angerechnet, Arbeiterzeiten zu 50 %. Die
              Höchstanrechnungsdauer beträgt 7 Jahre (für Verkauf &amp;
              Vertrieb mit Kassierarbeiten kommt 1 Jahr hinzu).
            </p>
          </div>
          <KVFaksimile
            kvName="KV Handel — Angestellte"
            paragraph="§ 4 Ziff. 3 · Anrechnung von Vordienstzeiten"
            quote="Für die Einstufung in eine Beschäftigungsgruppe sind sämtliche Zeiten einer einschlägigen Berufstätigkeit anzurechnen, höchstens jedoch 7 Jahre. Bei Angestellten in Verkauf und Vertrieb mit überwiegender Kassiertätigkeit erhöht sich diese Höchstdauer um 1 Jahr. Angestelltenzeiten werden voll, Arbeiterzeiten zur Hälfte angerechnet."
            note="Sarahs 3 Jahre 11 Monate als Angestellte im Einzelhandel liegen deutlich unter der 7-Jahres-Grenze — die Höchstanrechnungsdauer wirkt sich hier nicht aus. Ihre Berufsjahre fließen zu 100 % in die Gehaltsstufe ein."
          />
        </div>
      ),
    },
    {
      title: "Los geht's — Sie klicken sich durch",
      subtitle: "Rechts das echte Tool, links bleibt eine kurze Erklärung",
      body: (
        <div className="space-y-4 max-w-2xl">
          <p className="text-base text-ink-800 leading-relaxed">
            Sie sehen jetzt das echte Einstufungs-Tool. Ihre HR-Kolleginnen und
            Kollegen arbeiten damit täglich.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border border-ink-200 bg-white rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-brand-600 font-semibold mb-1">
                Links
              </div>
              <div className="text-sm text-ink-800">
                Kurze Erklärung pro Schritt — was passiert im Tool und worauf
                Sie achten können.
              </div>
            </div>
            <div className="border border-ink-200 bg-white rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-brand-600 font-semibold mb-1">
                Rechts
              </div>
              <div className="text-sm text-ink-800">
                Das interaktive Tool. Klicken, prüfen, editieren — genau wie in
                der Vollversion.
              </div>
            </div>
          </div>
          <div className="pt-2 text-sm text-warm-700">
            Klicken Sie unten auf{" "}
            <strong>»Tool starten →«</strong>, wenn Sie bereit sind.
          </div>
        </div>
      ),
    },
  ],

  storyPanels: [
    {
      step: 0,
      title: "Schritt 1 · KV & Stichtag",
      summary:
        "Der Kollektivvertrag ist die rechtliche Grundlage für die Einstufung. Der Stichtag ist der erste Arbeitstag im neuen Job.",
      bullets: [
        "KV Handel/Angestellte ist als Favorit markiert",
        "Stichtag = heute (22.7.2026)",
        "Haftungsausschluss bestätigen — dann weiter",
      ],
      hint: "Klicken Sie im Tool auf »Weiter zur Tätigkeit«.",
    },
    {
      step: 1,
      title: "Schritt 2 · Tätigkeit klassifizieren",
      summary:
        "ET vergleicht die Tätigkeitsbeschreibung mit der KV-Struktur und schlägt eine Beschäftigungsgruppe vor.",
      bullets: [
        "Die Beschreibung ist bereits eingetippt",
        "Ein Sicherheitswert zeigt, wie klar die Zuordnung ist",
        "Bei unklarer Zuordnung stellt ET Rückfragen",
      ],
      hint: "Klicken Sie auf »Auswertung starten« — dauert einige Sekunden.",
    },
    {
      step: 2,
      title: "Schritt 3 · Vordienstzeiten",
      summary:
        "Aus dem SV-Auszug hat ET Sarahs bisherige Beschäftigungen extrahiert und die KV-Anrechnungsfaktoren gesetzt.",
      bullets: [
        "2 Zeilen — beide 100 % anrechenbar laut KV Handel/Angestellte § 4 Ziff. 3",
        "Grünes Häkchen = klar geregelt, keine Prüfung nötig",
        "Bei Sonderfällen (Praktikum, manuelle Anpassung, Prüfhinweis) erscheinen entsprechende Marker",
      ],
      hint: "Klicken Sie auf »Empfehlung erstellen«.",
    },
    {
      step: 3,
      title: "Schritt 4 · Ihre Empfehlung",
      summary:
        "ET kombiniert KV + Tätigkeit + Vordienstzeiten und liefert eine begründete Einstufungs-Empfehlung.",
      bullets: [
        "Beschäftigungsgruppe & Gehaltsstufe",
        "Anrechenbare Dienstjahre (kombiniert)",
        "KV-referenzierte Begründung",
        "In der Vollversion: PDF-Bericht mit Audit-Trail",
      ],
      hint: "Wollen Sie es mit Ihren eigenen Daten probieren?",
    },
  ],
};

/* ═══════════════════════════════════════════════════════════════════════
 * JULIA — Komplex-Fall
 * ═══════════════════════════════════════════════════════════════════════ */
export const JULIA_STORY = {
  introSlides: [
    {
      title: "Julia wechselt in eine Führungsposition",
      subtitle: "Komplexe Einstufung nach KV Metallgewerbe",
      body: (
        <div className="space-y-4 max-w-2xl">
          <p className="text-base text-ink-800 leading-relaxed">
            <strong className="text-ink-900">Julia</strong> übernimmt bei
            Continental die Position <strong>Functional Safety Manager</strong>{" "}
            im Automotive-Bereich. Sie leitet ein interdisziplinäres Team,
            verantwortet Safety-Freigaben nach ISO 26262 und koordiniert
            externe Zertifizierungsstellen.
          </p>
          <p className="text-sm text-warm-700 leading-relaxed">
            Ihr Werdegang ist komplex: Praktikum, selbständige Zwischenphase,
            eine kurze fach­fremde Beschäftigung. All diese Sonderfälle muss
            die Einstufung sauber berücksichtigen — und die Summe der
            anrechenbaren Zeit trifft die Höchstanrechnungsdauer nach KV
            Metallgewerbe.
          </p>
          <div className="pt-2 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 font-semibold">
              KV Metallgewerbe
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 font-semibold">
              6 Vordienstzeiten
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 font-semibold">
              Höchstanrechnung erreicht
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Julias vielseitiger Werdegang",
      subtitle: "Von der Praktikantin zur Team-Leiterin — mit Zwischenstationen",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Julia hat einen typischen technischen Karriere-Weg hinter sich —
              Praktikum, Junior-Position, Führungsverantwortung, dazu eine
              frühere Phase als selbständige Consultant und eine kurze
              fach­fremde Beschäftigung im Consulting.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              Jede dieser Positionen wird im KV Metallgewerbe anders behandelt:
              einschlägige Angestelltenzeiten voll anrechenbar,
              Übergangs-Beschäftigungen zur Prüfung markiert, selbständige
              Zeiten gar nicht anrechenbar.
            </p>
          </div>
          <CVFaksimile
            personName="Mag. Julia Musterhofer"
            subtitle="Functional Safety Manager · Automotive"
            positions={[
              {
                zeitraum: "07/2021 – heute",
                arbeitgeber: "Continental AG Wien",
                taetigkeit: "Functional Safety Engineer",
              },
              {
                zeitraum: "09/2017 – 06/2021",
                arbeitgeber: "Robert Bosch AG",
                taetigkeit: "Teamleiterin Qualitätssicherung",
              },
              {
                zeitraum: "01/2017 – 08/2017",
                arbeitgeber: "Consulting XY",
                taetigkeit: "Sachbearbeiterin Projektmgmt.",
              },
              {
                zeitraum: "02/2014 – 12/2016",
                arbeitgeber: "Magna Steyr AG",
                taetigkeit: "Junior Systemingenieurin",
              },
              {
                zeitraum: "07/2013 – 12/2013",
                arbeitgeber: "AVL List GmbH",
                taetigkeit: "Praktikum Testfeld",
              },
              {
                zeitraum: "01/2011 – 06/2013",
                arbeitgeber: "Selbständig",
                taetigkeit: "Freelance Consulting",
              },
            ]}
            education={[
              {
                zeitraum: "2011 – 2014",
                einrichtung: "FH Joanneum",
                abschluss: "Master Automotive Engineering",
              },
            ]}
          />
        </div>
      ),
    },
    {
      title: "Der SV-Auszug — Julias verbindliche Datenquelle",
      subtitle: "ET liest den Auszug und ordnet jede Zeile automatisch ein",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Julias SV-Auszug enthält Angestelltenzeiten in
              Metallgewerbe-Betrieben ebenso wie eine fach­fremde
              Übergangs-Beschäftigung. ET erkennt beides und markiert
              zweifelhafte Zeilen zur Prüfung.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              Selbständige Zeiten sind laut KV Metallgewerbe § 15 Abs. 3 nicht
              anrechenbar — auch das erkennt ET automatisch und trägt Faktor
              0 % ein.
            </p>
            <div className="text-[11px] text-warm-500 pt-1">
              → Rechts sehen Sie einen Ausschnitt aus Julias SV-Auszug.
            </div>
          </div>
          <SVFaksimile
            personName="Musterhofer, Julia"
            highlightIndex={2}
            rows={[
              {
                von: "01.07.2021",
                bis: "laufend",
                arbeitgeber: "Continental AG Wien",
                art: "Angestellt · Vollzeit",
              },
              {
                von: "01.09.2017",
                bis: "30.06.2021",
                arbeitgeber: "Robert Bosch AG",
                art: "Angestellt · Vollzeit",
              },
              {
                von: "01.01.2017",
                bis: "31.08.2017",
                arbeitgeber: "Consulting XY",
                art: "Angestellt · Vollzeit",
              },
              {
                von: "01.02.2014",
                bis: "31.12.2016",
                arbeitgeber: "Magna Steyr AG",
                art: "Angestellt · Vollzeit",
              },
            ]}
          />
        </div>
      ),
    },
    {
      title: "KV Metallgewerbe — 12 Verwendungsgruppenjahre",
      subtitle: "Höchstanrechnungsdauer bei einschlägigen Vordienstzeiten",
      body: (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3 max-w-md">
            <p className="text-base text-ink-800 leading-relaxed">
              Der KV Metallgewerbe (Angestellte) sieht eine{" "}
              <strong>Höchstanrechnungsdauer von 12 Verwendungsgruppenjahren</strong>{" "}
              vor. Einschlägige Angestelltenzeiten werden voll auf die
              Gehaltsstufe angerechnet, bis diese Grenze erreicht ist.
            </p>
            <p className="text-sm text-warm-700 leading-relaxed">
              Julias einschlägige Zeiten übersteigen rechnerisch die 12 Jahre —
              die Höchstanrechnung wird also voll ausgeschöpft. Zusätzliche
              Vordienstzeiten würden die Einstufung nicht mehr verändern.
            </p>
          </div>
          <KVFaksimile
            kvName="KV Metallgewerbe — Angestellte"
            paragraph="§ 15 Abs. 3 · Anrechnung von Vordienstzeiten"
            quote="Zeiten einer facheinschlägigen Vordienstzeit werden bis zu einer Gesamtdauer von 12 Verwendungsgruppenjahren auf die Gehaltsstufe angerechnet. Als anrechenbar gelten nur nichtselbständige Beschäftigungsverhältnisse."
            note="Nach OGH-Auslegung zählen auch Tätigkeiten in anderen Branchen, sofern sie einer gleichen oder höheren Verwendungsgruppe entsprechen. Praktikum-Zeiten sind anrechenbar, wenn sie in einem sozialversicherungspflichtigen Angestellten- oder Arbeiter-Verhältnis erbracht wurden."
          />
        </div>
      ),
    },
    {
      title: "Bereit für den Komplex-Fall?",
      subtitle: "So sieht der Split-Screen-Modus aus",
      body: (
        <div className="space-y-4 max-w-2xl">
          <p className="text-base text-ink-800 leading-relaxed">
            Sie sehen gleich das interaktive Tool — mit dem gleichen Look-and-Feel
            wie in der Vollversion. Julias Komplex-Fall zeigt Ihnen, wie ET auch
            knifflige Konstellationen (Praktikum ohne Anstellungstyp, unklare
            Einschlägigkeit, selbständige Zeiten, Höchstanrechnung)
            sauber löst.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border border-ink-200 bg-white rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-brand-600 font-semibold mb-1">
                Links
              </div>
              <div className="text-sm text-ink-800">
                Kurze Erklärung pro Schritt — was passiert und worauf Sie
                achten können.
              </div>
            </div>
            <div className="border border-ink-200 bg-white rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-brand-600 font-semibold mb-1">
                Rechts
              </div>
              <div className="text-sm text-ink-800">
                Das interaktive Tool — Chips, Detail-Drawer, alle Editier-Optionen.
              </div>
            </div>
          </div>
          <div className="pt-2 text-sm text-warm-700">
            Klicken Sie unten auf <strong>»Tool starten →«</strong>.
          </div>
        </div>
      ),
    },
  ],

  storyPanels: [
    {
      step: 0,
      title: "Schritt 1 · KV & Stichtag",
      summary:
        "KV Metallgewerbe ist voreingestellt — hier gelten Cap-Regel und Bildungsbonus.",
      bullets: [
        "KV Metallgewerbe (Favorit-Markierung)",
        "Stichtag = heute (22.7.2026)",
        "Haftungsausschluss bestätigen",
      ],
      hint: "Klicken Sie im Tool auf »Weiter zur Tätigkeit«.",
    },
    {
      step: 1,
      title: "Schritt 2 · Tätigkeit klassifizieren",
      summary:
        "Führungsposition mit fachlicher Verantwortung — ET schlägt Beschäftigungsgruppe E vor.",
      bullets: [
        "Julia leitet ein Team und koordiniert externe Stellen",
        "Deutlich über Gruppe D, aber unter F",
        "Sicherheitswert ca. 87 % — solide Empfehlung",
      ],
      hint: "Klicken Sie auf »Auswertung starten«.",
    },
    {
      step: 2,
      title: "Schritt 3 · Vordienstzeiten",
      summary:
        "6 Zeilen mit verschiedenen Anrechnungslogiken — hier trennt sich Spreu vom Weizen.",
      bullets: [
        "Einschlägige Angestelltenzeilen: 100 % (grünes Häkchen)",
        "Übergangs-Beschäftigung: als »Annahme« markiert — bitte prüfen",
        "Praktikum: gelber Prüf-Marker — Anstellungstyp fehlt",
        "Selbständigkeit: 0 % (nicht anrechenbar laut KV § 15 Abs. 3)",
      ],
      hint: "Klicken Sie auf »Empfehlung erstellen«.",
    },
    {
      step: 3,
      title: "Schritt 4 · Ihre Empfehlung",
      summary:
        "Die Höchstanrechnung von 12 Verwendungsgruppenjahren ist erreicht — weitere Zeiten würden das Ergebnis nicht mehr verändern.",
      bullets: [
        "Beschäftigungsgruppe E, Gehaltsstufe 6",
        "Anrechnung = 12 Jahre (Höchstdauer laut KV § 15 Abs. 3)",
        "Begründung verweist auf die einschlägigen Zeiten und die Höchstgrenze",
        "In der Vollversion: PDF-Bericht mit KV-Referenzen",
      ],
      hint: "Wollen Sie es mit Ihren eigenen Daten probieren?",
    },
  ],
};

export const STORIES = { sarah: SARAH_STORY, julia: JULIA_STORY };
export function getStory(caseId) {
  return STORIES[caseId] || null;
}
